// src/context/LangContext.jsx
import React, { createContext, useState, useEffect } from 'react'
import { translations } from '../i18n/translations'
import { detectUserLanguage, detectLanguageFromURL, detectLanguageByLocation } from '../utils/languageDetection'

export const LangContext = createContext({
  lang: 'de',
  setLang: () => {},
  t: (key) => key
})

function isIRContext() {
  if (typeof window === 'undefined') return false;
  const host = (window.location.hostname || '').toLowerCase();
  const path = (window.location.pathname || '').toLowerCase();
  const qp = new URLSearchParams(window.location.search);
  const forced = qp.get('ir');
  if (String(process.env.REACT_APP_FORCE_IR).toLowerCase() === 'true') return true;
  if (forced === '1' || forced === 'true') return true;
  const parts = host.split('.');
  const first = parts[0] || '';
  const second = parts[1] || '';
  const isIrSub = first === 'ir' || (first === 'www' && second === 'ir');
  const isIrPath = path === '/ir' || path.startsWith('/ir/');
  return isIrSub || isIrPath;
}

function getAllowedLanguages() {
  return isIRContext() ? ['en', 'it', 'es', 'pt'] : ['de', 'es', 'it'];
}

export function LangProvider({ children }) {
  // Initialize language with smart detection
  const [lang, setLang] = useState(() => {
    const allowed = getAllowedLanguages();
    // Priority 1: URL parameter (for direct links like ?lang=it)
    const urlLang = detectLanguageFromURL();
    if (urlLang && allowed.includes(urlLang)) return urlLang;
    
    // Priority 2: Saved preference in localStorage
    const savedLang = localStorage.getItem('centeo-language');
    if (savedLang && allowed.includes(savedLang)) return savedLang;
    
    // Priority 3: Browser language detection
    const detected = detectUserLanguage();
    return allowed.includes(detected) ? detected : allowed[0];
  });

  // Auto-detect language on first visit (when no saved preference exists)
  useEffect(() => {
    const allowed = getAllowedLanguages();
    const savedLang = localStorage.getItem('centeo-language');
    const urlLang = detectLanguageFromURL();
    
    // Only auto-detect if no saved preference and no URL parameter
    if (!savedLang && !urlLang) {
      // Try geolocation-based detection (with user permission)
      detectLanguageByLocation().then(detectedLang => {
        const next = allowed.includes(detectedLang) ? detectedLang : allowed[0];
        if (next && next !== lang) {
          console.log('Auto-detected language based on location:', next);
          setLang(next);
          localStorage.setItem('centeo-language', next);
        }
      });
    }
  }, [lang]);

  // Save language to localStorage when it changes
  const setLanguage = (newLang) => {
    const allowed = getAllowedLanguages();
    const next = allowed.includes(newLang) ? newLang : allowed[0];
    setLang(next);
    localStorage.setItem('centeo-language', next);
    console.log('Language changed to:', next);
  };

  // Übersetzungs-Funktion: "hero.title" → translations[lang].hero.title
  const t = (key) => {
    // Handle simple keys first (like "q2Options")
    if (translations[lang] && translations[lang][key] != null) {
      return translations[lang][key];
    }
    
    // Handle nested keys with dots (like "hero.title")
    return key
      .split('.')
      .reduce((obj, k) => (obj && obj[k] != null ? obj[k] : null), translations[lang])
      || key
  }

  return (
    <LangContext.Provider value={{ lang, setLang: setLanguage, t }}>
      {children}
    </LangContext.Provider>
  );
}
